import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { lbToast } from '@linearb/buzz'
import { queryKeys } from '../lib/queryKeys'
import {
  behindLabel,
  CURRENT_VERSION,
  isTagVersion,
  parseTag,
  VERSION_JSON_URL,
  versionsBehind
} from '../lib/appVersion'
import { trackAction } from '../lib/datadogRum'

const POLL_MS = 30 * 60 * 1000
const DISMISSED_KEY = 'dismissed_app_version'

interface LatestVersion {
  latestVersion: string
  behind: number
}

export const useLatestVersion = (): LatestVersion => {
  const { data } = useQuery({
    queryKey: queryKeys.appVersion.latest(),
    queryFn: async () => {
      const res = await fetch(`${VERSION_JSON_URL}?t=${Date.now()}`, {
        cache: 'no-store'
      })
      // Builds deployed before version.json existed 404 here — stay quiet.
      return res.ok ? await res.json() : null
    },
    enabled: isTagVersion(CURRENT_VERSION),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Matches the poll interval so remounts and tab focus don't refetch on
    // every navigation — the footer mounts this hook on nearly every route.
    staleTime: POLL_MS
  })

  const latestVersion = parseTag(data?.tag || '')

  return {
    latestVersion,
    behind: versionsBehind(latestVersion, CURRENT_VERSION)
  }
}

export const reloadForUpdate = (latestVersion: string, behind: number): void => {
  trackAction('app_update_reload_clicked', {
    from: CURRENT_VERSION,
    to: latestVersion,
    behind
  })
  window.location.reload()
}

export const useVersionUpdateToast = (): void => {
  const { latestVersion, behind } = useLatestVersion()
  // buzz exposes no dismiss API, so a later release would stack a second prompt
  // on top of an unread one. The footer keeps showing the live count instead.
  const toastOpen = useRef(false)
  // A persistent toast outlives the render that created it, so its callbacks
  // must read the current version — not the one captured when it opened.
  const current = useRef({ latestVersion, behind })

  useEffect(() => {
    current.current = { latestVersion, behind }
  }, [latestVersion, behind])

  useEffect(() => {
    if (!behind || toastOpen.current) {
      return
    }

    if (localStorage.getItem(DISMISSED_KEY) === latestVersion) {
      return
    }

    toastOpen.current = true

    lbToast(
      {
        title: '🎉 New version available',
        description: `You're ${behindLabel(behind)}`,
        closeButton: true,
        button: {
          label: 'Reload',
          onClick: () =>
            reloadForUpdate(current.current.latestVersion, current.current.behind)
        }
      },
      {
        duration: Infinity,
        onDismiss: () => {
          toastOpen.current = false
          localStorage.setItem(DISMISSED_KEY, current.current.latestVersion)
          trackAction('app_update_dismissed', {
            from: CURRENT_VERSION,
            to: current.current.latestVersion,
            behind: current.current.behind
          })
        }
      }
    )
  }, [latestVersion, behind])
}
